<script setup lang="ts">
import type { Category, LikeableResource } from '@/models'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { categoryColour } from '@/constants'
import LikeButton from '@/components//LikeButton.vue'
import Article from '@/assets/subcategories/Article.svg'
import Book from '@/assets/subcategories/Book.svg'
import Guide from '@/assets/subcategories/Guide.svg'
import Survey from '@/assets/subcategories/Survey.svg'
import Tools from '@/assets/subcategories/Tools.svg'
import Video from '@/assets/subcategories/Video.svg'
import { i18n } from '@/i18n'
const t = i18n.global.t

const props = defineProps<{
  category?: Category
  resource: LikeableResource
  liked: boolean
  likeMode: 'build' | 'like' | 'none'
  mode: 'build' | 'regular'
}>()

const emit = defineEmits<{
  (e: 'like', value: string): void
}>()

const menu = ref(false)
const menuPosition = ref({ top: 0, left: 0 })
const buttonRef = ref<HTMLElement | null>(null)

function toggleMenu(open: boolean) {
  menu.value = open
  if (open && buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect()
    menuPosition.value = {
      top: rect.bottom,
      left: rect.left,
    }
  }
}

function redirectToPage(url: string) {
  window.open(url, '_blank')
}
const subcategorySource = computed(() => {
  switch (props.resource.subcategory) {
    case 'e-book':
      return Book
    case 'guideline':
      return Guide
    case 'other':
      return Survey
    case 'tool':
      return Tools
    case 'video':
      return Video
    case 'article':
    default:
      return Article
  }
})

const subcategoryLabel = computed(() => {
  switch (props.resource.subcategory) {
    case 'e-book':
      return 'Book'
    case 'guideline':
      return 'Guide'
    case 'other':
      return 'Survey'
    case 'tool':
      return 'Tools'
    case 'video':
      return 'Video'
    case 'article':
    default:
      return 'Article'
  }
})

function handleClickOutside() {
  menu.value = false
}

onMounted(() => {
  document.addEventListener('pointerup', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('pointerup', handleClickOutside)
})

const firstFile = props.resource.files?.[0]?.url ?? ''
</script>

<template>
  <div
    class="flex flex-col items-center text-center w-full max-w-64 h-70 rounded-2xl border border-[#DBD2D2] shadow-md/30 aspect-square bg-white"
  >
    <div
      v-if="mode === 'build'"
      class="min-h-12 w-full rounded-t-2xl"
      :class="categoryColour(props.category).background + ' ' + categoryColour(props.category).text"
    ></div>
    <div class="w-full flex flex-col items-start text-left grow p-2 px-3">
      <p v-if="mode === 'regular'" class="font-semibold text-md mt-2 line-clamp-2">
        {{ resource.title }}
      </p>
      <!-- Resource type badge: icon + subcategory label -->
      <div class="flex flex-row items-center my-2">
        <img :src="subcategorySource" class="w-4 h-4 mr-2" />
        <p class="text-xs">{{ subcategoryLabel }}</p>
      </div>
      <p v-if="mode === 'build'" class="font-semibold w-full text-md line-clamp-2">
        {{ resource.title }}
      </p>
      <p class="font-normal line-clamp-3 text-sm mt-2">
        {{ resource.description }}
      </p>
    </div>
    <!-- Download or read more - file or URL TODO: needs to deal with MIME types. i -->
    <div class="w-full flex flex-row items-center justify-between p-3 self-end">
      <button
        v-if="firstFile"
        class="flex items-center justify-center gap-x-2 p-2 px-4 shadow-md lowercase text-nowrap [font-variant:small-caps] max-lg:rounded-full max-lg:w-6 max-lg:h-6 max-lg:p-0"
        :class="[
          props.mode === 'build'
            ? 'bg-green text-white'
            : categoryColour(props.category).background + ' ' + categoryColour(props.category).text,
        ]"
        @click="redirectToPage(firstFile)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          width="24"
          height="24"
          class="w-3 h-3"
        >
          <!-- Arrow down -->
          <path d="M12 5v14"></path>
          <path d="M5 12l7 7 7-7"></path>
          <!-- Base line -->
          <path d="M5 19h14"></path>
        </svg>
        <span class="max-lg:hidden">{{ t('download') }}</span>
      </button>
      <button
        v-else-if="resource.url"
        class="flex items-center justify-center gap-x-2 p-2 px-4 bg-green text-white shadow-md lowercase [font-variant:small-caps] max-lg:rounded-full max-lg:w-6 max-lg:h-6 max-lg:p-0"
        @click="redirectToPage(resource.url)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-3 h-3"
        >
          <path d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6z" />
          <circle cx="12" cy="12" r="2.5" />
        </svg>
        <span class="max-lg:hidden">{{ t('readMore') }}</span>
      </button>
      <!-- Like button and options menu: state-based styling -->
      <div class="flex flex-row" v-if="likeMode !== 'none'">
        <div
          @click="emit('like', props.resource.documentId)"
          class="ml-4 h-12 rounded-full flex items-center justify-center cursor-pointer"
        >
          <LikeButton class="w-6 h-6" :liked="liked" />
        </div>

        <div
          ref="buttonRef"
          @pointerdown.prevent="toggleMenu(true)"
          @pointerup.prevent="toggleMenu(false)"
          class="ml-4 h-12 rounded-full flex items-center justify-center cursor-pointer"
        >
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </div>
      </div>
    </div>

    <!-- The menu is marked 'fixed' to avoid problems with parent overlap -->
    <div
      v-if="menu"
      class="fixed rounded-2xl p-4 flex flex-col bg-white min-w-32 border shadow-lg z-50"
      :style="{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }"
    >
      <p class="hover:bg-black hover:text-white whitespace-nowrap rounded-sm py-1 px-2">
        Menu item 1 coming soon!
      </p>
      <p class="hover:bg-black hover:text-white whitespace-nowrap rounded-sm py-1 px-2">
        Menu item 2 coming soon!
      </p>
    </div>
  </div>
</template>
